// Interruptible spring animation driven by requestAnimationFrame.
//
// Parameterised the way Apple's UIKit/SwiftUI springs are:
//   dampingRatio — 1.0 = critically damped (no overshoot), < 1 = bouncy
//   response     — roughly how long (seconds) it takes to reach the target
// The spring always continues from its current value and velocity, so it can be
// re-targeted mid-flight (e.g. a sheet grabbed while it is still opening).

export interface SpringConfig {
  dampingRatio: number;
  response: number;
}

export const SPRING_DEFAULT: SpringConfig = { dampingRatio: 1, response: 0.38 };
export const SPRING_SNAPPY: SpringConfig = { dampingRatio: 1, response: 0.3 };
export const SPRING_MOMENTUM: SpringConfig = { dampingRatio: 0.82, response: 0.32 };

const STEP = 1 / 240; // fixed integration step (s)
const REST_VELOCITY = 4; // px/s
const REST_DISTANCE = 0.4; // px

export class Spring {
  value: number;
  velocity = 0;
  target: number;
  private config: SpringConfig = SPRING_DEFAULT;
  private raf = 0;
  private lastTime = 0;
  private onRest?: () => void;

  constructor(initial: number, private onUpdate: (value: number) => void) {
    this.value = initial;
    this.target = initial;
  }

  get isAnimating() {
    return this.raf !== 0;
  }

  /** Jump to a value with no animation (used while the finger is driving it). */
  set(value: number, velocity = 0) {
    this.stop();
    this.value = value;
    this.target = value;
    this.velocity = velocity;
    this.onUpdate(value);
  }

  /** Animate to target, starting from the current value. Pass a velocity to hand off a gesture. */
  animateTo(target: number, config: SpringConfig = SPRING_DEFAULT, velocity?: number, onRest?: () => void) {
    this.target = target;
    this.config = config;
    this.onRest = onRest;
    if (velocity !== undefined) this.velocity = velocity;
    if (!this.raf) {
      this.lastTime = performance.now();
      this.raf = requestAnimationFrame(this.tick);
    }
  }

  stop() {
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = 0;
  }

  private tick = (now: number) => {
    // Clamp long frames (tab switch, debugger) so the integration stays stable.
    let dt = Math.min((now - this.lastTime) / 1000, 1 / 20);
    this.lastTime = now;

    const { dampingRatio, response } = this.config;
    const stiffness = Math.pow((2 * Math.PI) / response, 2);
    const damping = (4 * Math.PI * dampingRatio) / response;

    while (dt > 0) {
      const h = Math.min(STEP, dt);
      const force = -stiffness * (this.value - this.target) - damping * this.velocity;
      this.velocity += force * h;
      this.value += this.velocity * h;
      dt -= h;
    }

    if (Math.abs(this.velocity) < REST_VELOCITY && Math.abs(this.value - this.target) < REST_DISTANCE) {
      this.value = this.target;
      this.velocity = 0;
      this.raf = 0;
      this.onUpdate(this.value);
      const done = this.onRest;
      this.onRest = undefined;
      done?.();
      return;
    }

    this.onUpdate(this.value);
    this.raf = requestAnimationFrame(this.tick);
  };
}

/** Where a flick would come to rest under scroll-style deceleration (Apple's projection function). */
export function projectMomentum(velocity: number, decelerationRate = 0.998): number {
  return ((velocity / 1000) * decelerationRate) / (1 - decelerationRate);
}

/** Progressive resistance past a boundary. */
export function rubberband(overshoot: number, dimension: number, constant = 0.55): number {
  return (overshoot * dimension * constant) / (dimension + constant * Math.abs(overshoot));
}

/** Tracks recent pointer samples so release velocity reflects the end of the gesture, not its average. */
export class VelocityTracker {
  private samples: { t: number; v: number }[] = [];

  reset() {
    this.samples = [];
  }

  add(value: number, t = performance.now()) {
    this.samples.push({ t, v: value });
    const cutoff = t - 100;
    while (this.samples.length > 2 && this.samples[0].t < cutoff) this.samples.shift();
  }

  /** px/s */
  velocity(): number {
    const s = this.samples;
    if (s.length < 2) return 0;
    const first = s[0];
    const last = s[s.length - 1];
    const dt = (last.t - first.t) / 1000;
    if (dt <= 0) return 0;
    // Finger stopped before lifting — no momentum.
    if (performance.now() - last.t > 80) return 0;
    return (last.v - first.v) / dt;
  }
}
